import React, { useEffect, useMemo, useState } from 'react'
import { color, font, size, space, weight } from './verificationTheme'
import { fingerprintForWidth } from './panelLayout'
import type { CompositeSummary } from './ledger/composite'

// The slice under the playhead, in full: which one it is, when it runs, the
// whole fingerprint rather than the eight characters the chart prints, and what
// it was checked against.
//
// This is where a sceptical viewer goes. The chart shows that checking is
// happening; this shows what a single check actually consists of, and it is the
// only place in the panel a complete hash appears. Read once a second — a slice
// lasts several seconds, so there is nothing here that needs a frame loop.

const TICK_MS = 1000

export interface SliceDetailProps {
  summary: CompositeSummary
  /** Ref-backed and stable, straight from the player. */
  getTime: () => number
  frozen?: boolean
  /** A phone-width panel. The full 64-character fingerprint wraps to five
   * lines there and pushes everything below it off the screen. */
  narrow?: boolean
}

function clock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const whole = Math.floor(seconds)
  const m = Math.floor(whole / 60)
  const s = whole % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <>
    <div style={{ opacity: 0.55, paddingRight: space.md }}>{label}</div>
    <div>{children}</div>
  </>
)

const Mono: React.FC<{ children: React.ReactNode; tone?: string }> = ({ children, tone }) => (
  <span style={{ fontFamily: font.mono, fontSize: size.mono, color: tone ?? color.bone, wordBreak: 'break-all' }}>
    {children}
  </span>
)

export default function SliceDetail({ summary, getTime, frozen = false, narrow = false }: SliceDetailProps) {
  const [now, setNow] = useState(() => getTime())

  useEffect(() => {
    if (frozen) return undefined
    const timer = window.setInterval(() => setNow(getTime()), TICK_MS)
    return () => window.clearInterval(timer)
  }, [frozen, getTime])

  const cell = useMemo(
    () => summary.cells.find((c) => c.start <= now && c.end > now) ?? null,
    [summary, now],
  )

  if (!cell) return null

  const mismatch = cell.state === 'mismatch'
  const checked = cell.state !== 'unchecked'

  return (
    <div
      style={{
        display: 'grid',
        // One column when narrow: a label and a 17-character fingerprint do
        // not fit side by side on a phone, and squeezing them there is what
        // made this panel unreadable.
        gridTemplateColumns: narrow ? '1fr' : 'max-content 1fr',
        rowGap: narrow ? 2 : space.xs,
        alignItems: 'baseline',
        fontFamily: font.prose,
        fontSize: size.body,
        lineHeight: 1.5,
      }}
    >
      <Row label="Slice">
        <Mono>
          {cell.index + 1} of {summary.totalCount}
        </Mono>
        <span style={{ opacity: 0.55 }}>
          {'  '}
          {clock(cell.start)} to {clock(cell.end)}
        </span>
      </Row>

      <Row label="Fingerprint">
        {cell.hash ? (
          <Mono tone={mismatch ? color.alarm : color.bone}>{fingerprintForWidth(cell.hash, narrow)}</Mono>
        ) : (
          <span style={{ opacity: 0.55 }}>
            {checked ? 'Checked, but no single fingerprint belongs to this slice.' : 'Not checked yet.'}
          </span>
        )}
      </Row>

      <Row label="Checked against">
        {checked ? (
          <>
            <Mono>{cell.by.join(', ') || '—'}</Mono>
            {cell.by.length > 1 && (
              <span style={{ opacity: 0.55 }}>{'  '}two sealed manifests agreed</span>
            )}
          </>
        ) : (
          <span style={{ opacity: 0.55 }}>Nothing yet. Its bytes have not been checked.</span>
        )}
      </Row>

      {summary.active && (
        <Row label="Streaming now">
          <Mono>{summary.active}</Mono>
        </Row>
      )}

      {mismatch && (
        <Row label="Result">
          <span style={{ color: color.alarm, fontWeight: weight.semibold }}>
            This slice did not match the sealed record.
          </span>
        </Row>
      )}
    </div>
  )
}
